/**
 * Contextual outbound links to MaintenanceSpec, the specialist destination for
 * manufacturer-based maintenance schedules.
 *
 * Single source of truth: only makes/models that have a PUBLISHED schedule on
 * MaintenanceSpec appear here, so no template can link to a missing page. Links
 * are rendered only in maintenance context (make hub, brand maintenance page,
 * a matching model page, the maintenance-schedule data page, the maintenance
 * hub, and maintenance articles) — never sitewide.
 */

export const MAINTENANCE_SPEC_HOME = "https://maintenancespec.com/";
export const MAINTENANCE_SPEC_FINDER = "https://maintenancespec.com/find-my-vehicle/";

interface MakeEntry {
  makeUrl: string;
  /** AutoSpecData model slug -> published MaintenanceSpec schedule URL. */
  models: Record<string, string>;
}

export const maintenanceSpecLinks: Record<string, MakeEntry> = {
  toyota: {
    makeUrl: "https://maintenancespec.com/toyota/",
    models: {
      rav4: "https://maintenancespec.com/toyota/rav4-maintenance-schedule/",
      "gr-supra": "https://maintenancespec.com/toyota/gr-supra-maintenance-schedule/",
      "prius-c": "https://maintenancespec.com/toyota/prius-c-maintenance-schedule/",
    },
  },
  honda: {
    makeUrl: "https://maintenancespec.com/honda/",
    models: { pilot: "https://maintenancespec.com/honda/pilot-maintenance-schedule/" },
  },
  ford: {
    makeUrl: "https://maintenancespec.com/ford/",
    models: { fiesta: "https://maintenancespec.com/ford/fiesta-maintenance-schedule/" },
  },
  rivian: {
    makeUrl: "https://maintenancespec.com/rivian/",
    models: { r2: "https://maintenancespec.com/rivian/r2-maintenance-schedule/" },
  },
  tesla: {
    makeUrl: "https://maintenancespec.com/tesla/",
    models: { "model-3": "https://maintenancespec.com/tesla/model-3-maintenance-schedule/" },
  },
  cadillac: {
    makeUrl: "https://maintenancespec.com/cadillac/",
    models: { srx: "https://maintenancespec.com/cadillac/srx-maintenance-schedule/" },
  },
  dodge: {
    makeUrl: "https://maintenancespec.com/dodge/",
    models: { hornet: "https://maintenancespec.com/dodge/hornet-maintenance-schedule/" },
  },
  jeep: {
    makeUrl: "https://maintenancespec.com/jeep/",
    models: { gladiator: "https://maintenancespec.com/jeep/gladiator-maintenance-schedule/" },
  },
  polestar: {
    makeUrl: "https://maintenancespec.com/polestar/",
    models: { "4": "https://maintenancespec.com/polestar/polestar-4-maintenance-schedule/" },
  },
};

export function maintenanceSpecMakeUrl(makeSlug: string): string | null {
  return maintenanceSpecLinks[makeSlug]?.makeUrl ?? null;
}

export function maintenanceSpecModelUrl(makeSlug: string, modelSlug: string): string | null {
  return maintenanceSpecLinks[makeSlug]?.models[modelSlug] ?? null;
}

/** Maintenance-topic articles -> the matching MaintenanceSpec resource. */
export const maintenanceSpecArticleLinks: Record<string, { url: string; anchor: string }> = {
  "maintenance-intervals-that-matter": {
    url: "https://maintenancespec.com/maintenance-mileage-vs-time/",
    anchor: "how mileage and time intervals interact",
  },
  "maintenance-oil-change-guide": {
    url: "https://maintenancespec.com/maintenance-oil-change/",
    anchor: "oil change service intervals",
  },
  "severe-driving-conditions": {
    url: "https://maintenancespec.com/maintenance-special-driving-conditions/",
    anchor: "severe driving condition schedules",
  },
  "maintenance-codes-explained": {
    url: "https://maintenancespec.com/maintenance-maintenance-code-systems/",
    anchor: "maintenance code systems",
  },
};

export function maintenanceSpecArticleLink(slug: string) {
  return maintenanceSpecArticleLinks[slug] ?? null;
}
